import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';

// Reusable toggle switch (clean, no icons)
const Toggle = ({ checked, onChange, disabled, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div className="pr-4">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="text-sm text-gray-500">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
);

const TABS = [
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security & Login' },
  { id: 'privacy', label: 'Privacy & Data' }
];

const DEFAULT_SETTINGS = {
  notifications: {
    emailCampaignUpdates: true,
    emailMessages: true,
    emailPayments: true,
    emailMarketing: false,
    pushCampaignUpdates: true,
    pushMessages: true,
    pushPayments: true,
    deadline3DaysBefore: true,
    deadline1DayBefore: true,
    deadlineOnDay: true,
    deadlineOverdue: true
  },
  privacy: {
    publicProfile: true,
    showInDirectory: true,
    allowBrandInquiries: true,
    showEarnings: false
  }
};

const Settings = () => {
  const { currentUser, logout } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Account deletion state
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSettings({
            notifications: { ...DEFAULT_SETTINGS.notifications, ...(userData.settings?.notifications || {}) },
            privacy: { ...DEFAULT_SETTINGS.privacy, ...(userData.settings?.privacy || {}) }
          });
        }
      } catch (err) {
        setError('Failed to load settings. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [currentUser]);

  const handleNotificationChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const handlePrivacyChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      privacy: { ...prev.privacy, [key]: value }
    }));
  };

  const handleSaveSettings = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        settings,
        updatedAt: new Date()
      });
      showSuccess('Settings saved successfully.', 'Your preferences have been updated.', 3000);
    } catch (err) {
      showError('Failed to save settings.', 'Please check your connection and try again.', 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentUser || !auth.currentUser) {
      showError('Authentication error.', 'Please log in again and try.', 5000);
      return;
    }

    if (newPassword.length < 6) {
      showError('Password too short.', 'New password must be at least 6 characters.', 4000);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match.', 'Please make sure the new password and confirmation match.', 4000);
      return;
    }

    setChangingPassword(true);
    try {
      // Reauthenticate the user with their current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update the password
      await updatePassword(auth.currentUser, newPassword);

      showSuccess('Password updated successfully.', 'You may need to log in again with your new password.', 5000);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err.code === 'auth/wrong-password'
        ? 'Current password is incorrect. Please try again.'
        : err.code === 'auth/too-many-requests'
        ? 'Too many attempts. Please try again later.'
        : 'Failed to update password. Please try again.';
      showError('Password change failed.', 'Please try again or contact support if the problem persists.', 5000);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDownloadData = async () => {
    if (!currentUser) return;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `account-data-${currentUser.uid}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess('Data download started.', 'Your account data has been exported.', 3000);
      } else {
        showError('No data found.', 'Your account data could not be found.', 4000);
      }
    } catch (err) {
      showError('Failed to download data.', 'Please try again later.', 5000);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showError('Confirmation required.', 'Please type "DELETE" to confirm account deletion.', 4000);
      return;
    }

    setDeletingAccount(true);
    try {
      if (!auth.currentUser) {
        showError('Authentication error.', 'Please log in again and try.', 5000);
        return;
      }

      const userDocRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userDocRef);

      await deleteUser(auth.currentUser);

      showSuccess('Account deleted.', 'Your account has been permanently deleted.', 5000);
      await logout();
    } catch (err) {
      const message = err.code === 'auth/requires-recent-login'
        ? 'For security, please log out and log back in before deleting your account.'
        : 'Failed to delete account. Please try again or contact support.';
      showError('Account deletion failed.', 'Please try again or contact support if the problem persists.', 5000);
    } finally {
      setDeletingAccount(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Settings</h2>
          <p className="text-gray-600">Manage your account preferences</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Settings</h2>
        <p className="text-gray-600">Manage your account preferences</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Email Notifications</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle
                    label="Campaign Updates"
                    description="Get notified when campaign statuses change or new campaigns match your profile"
                    checked={settings.notifications.emailCampaignUpdates}
                    onChange={(v) => handleNotificationChange('emailCampaignUpdates', v)}
                  />
                  <Toggle
                    label="Messages"
                    description="Receive email alerts when brands send you messages"
                    checked={settings.notifications.emailMessages}
                    onChange={(v) => handleNotificationChange('emailMessages', v)}
                  />
                  <Toggle
                    label="Payments"
                    description="Get email notifications for payment updates and earnings"
                    checked={settings.notifications.emailPayments}
                    onChange={(v) => handleNotificationChange('emailPayments', v)}
                  />
                  <Toggle
                    label="Marketing & Tips"
                    description="Receive platform updates, tips, and promotional content"
                    checked={settings.notifications.emailMarketing}
                    onChange={(v) => handleNotificationChange('emailMarketing', v)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Push Notifications</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle
                    label="Campaign Updates"
                    description="In-app notifications for campaign status changes"
                    checked={settings.notifications.pushCampaignUpdates}
                    onChange={(v) => handleNotificationChange('pushCampaignUpdates', v)}
                  />
                  <Toggle
                    label="Messages"
                    description="In-app notifications for new messages"
                    checked={settings.notifications.pushMessages}
                    onChange={(v) => handleNotificationChange('pushMessages', v)}
                  />
                  <Toggle
                    label="Payments"
                    description="In-app notifications for payment updates"
                    checked={settings.notifications.pushPayments}
                    onChange={(v) => handleNotificationChange('pushPayments', v)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Deadline Reminders</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle
                    label="3 Days Before Deadline"
                    description="Get notified 3 days before campaign deadlines"
                    checked={settings.notifications.deadline3DaysBefore}
                    onChange={(v) => handleNotificationChange('deadline3DaysBefore', v)}
                  />
                  <Toggle
                    label="1 Day Before Deadline"
                    description="Get notified 1 day before campaign deadlines"
                    checked={settings.notifications.deadline1DayBefore}
                    onChange={(v) => handleNotificationChange('deadline1DayBefore', v)}
                  />
                  <Toggle
                    label="On Deadline Day"
                    description="Get notified on the day of the deadline"
                    checked={settings.notifications.deadlineOnDay}
                    onChange={(v) => handleNotificationChange('deadlineOnDay', v)}
                  />
                  <Toggle
                    label="Overdue Reminders"
                    description="Get reminded about overdue submissions"
                    checked={settings.notifications.deadlineOverdue}
                    onChange={(v) => handleNotificationChange('deadlineOverdue', v)}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Security & Login Tab */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Account Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm font-medium text-gray-900">{currentUser?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">Email Verified</span>
                    <span className={`text-sm font-medium ${currentUser?.emailVerified ? 'text-green-600' : 'text-amber-600'}`}>
                      {currentUser?.emailVerified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">User ID</span>
                    <span className="text-sm font-mono text-gray-400">{currentUser?.uid?.slice(0, 8)}...</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Change Password</h3>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                      placeholder="Enter your current password"
                    />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength="6"
                      className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength="6"
                      className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                      placeholder="Re-enter your new password"
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {changingPassword ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Danger Zone</h3>
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-gray-900">Delete Account</p>
                      <p className="text-sm text-gray-500 mt-1">Permanently delete your influencer account, portfolio, applications, and all associated data. This action cannot be undone.</p>
                    </div>
                    <button type="button" onClick={() => setShowDeleteModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors whitespace-nowrap">
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Privacy & Data Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Profile Visibility</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle
                    label="Public Profile"
                    description="Allow brands to view your profile and portfolio"
                    checked={settings.privacy.publicProfile}
                    onChange={(v) => handlePrivacyChange('publicProfile', v)}
                  />
                  <Toggle
                    label="Show in Directory"
                    description="Appear in the influencer directory for brand discovery"
                    checked={settings.privacy.showInDirectory}
                    onChange={(v) => handlePrivacyChange('showInDirectory', v)}
                  />
                  <Toggle
                    label="Allow Brand Inquiries"
                    description="Let brands contact you directly for collaborations"
                    checked={settings.privacy.allowBrandInquiries}
                    onChange={(v) => handlePrivacyChange('allowBrandInquiries', v)}
                  />
                  <Toggle
                    label="Show Earnings Stats"
                    description="Display earnings summary on your public profile"
                    checked={settings.privacy.showEarnings}
                    onChange={(v) => handlePrivacyChange('showEarnings', v)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Your Data</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Download Your Data</p>
                      <p className="text-sm text-gray-500">Export a copy of your account data as JSON</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadData}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account Deletion Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Account</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently delete your influencer account, portfolio, applications, payment records, and all associated data. This action <strong>cannot be undone</strong>.
            </p>
            <div className="mb-4">
              <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
              </label>
              <input
                type="text"
                id="deleteConfirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-150 ease-in-out"
                placeholder="DELETE"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="inline-flex justify-center py-2.5 px-6 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteAccount} disabled={deletingAccount || deleteConfirmText !== 'DELETE'} className="inline-flex justify-center py-2.5 px-6 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {deletingAccount ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
